import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { translations } from '@/data/translations';
import { useCategories } from '@/hooks/useSupabase';
import { Separator } from '@/components/ui/separator';
import { Facebook } from 'lucide-react';

// Pinterest icon (not available in lucide-react)
function PinterestIcon({ className }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="currentColor" 
      className={className}
    >
      <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
    </svg>
  );
}

export function Footer() {
  const { t, getLocalizedField } = useLanguage();
  const { categories } = useCategories();

  const companyLinks = [
    { to: '/', label: t(translations.nav.home) },
    { to: '/craft', label: t(translations.nav.craft) },
    { to: '/contact', label: t(translations.nav.contact) },
  ];

  return (
    <footer className="bg-[#2C2C2C] text-white" data-testid="footer">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link 
              to="/" 
              className="font-serif text-2xl tracking-tight text-white hover:text-[#8B5A2B] transition-colors"
              data-testid="footer-logo"
            >
              Helmut Santifaller
            </Link>
            <p className="mt-4 text-sm text-white/60 leading-relaxed font-sans">
              {t(translations.footer.tagline1)}<br />
              {t(translations.footer.tagline2)}
            </p>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="font-sans text-sm uppercase tracking-widest text-white/40 mb-6">
              {t(translations.footer.company)}
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="font-sans text-sm text-white/80 hover:text-[#8B5A2B] transition-colors"
                    data-testid={`footer-${link.to.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Catalog Column */}
          <div>
            <h4 className="font-sans text-sm uppercase tracking-widest text-white/40 mb-6">
              {t(translations.footer.catalog)}
            </h4>
            <ul className="space-y-3">
              {categories.slice(0, 6).map((category) => (
                <li key={category.slug}>
                  <Link
                    to={`/catalog?category=${category.slug}`}
                    className="font-sans text-sm text-white/80 hover:text-[#8B5A2B] transition-colors"
                    data-testid={`footer-cat-${category.slug}`}
                  >
                    {getLocalizedField(category, 'name')}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-sans text-sm uppercase tracking-widest text-white/40 mb-6">
              {t(translations.footer.contactInfo)}
            </h4>
            <address className="not-italic font-sans text-sm text-white/80 space-y-3">
              <p>{t(translations.contact.addressLine1)}</p>
              <p>{t(translations.contact.addressLine2)}</p>
              <p>{t(translations.contact.addressLine3)}</p>
              <p>{t(translations.contact.addressLine4)}</p>
              <p className="pt-2">
                <a href="tel:+393356917918" className="hover:text-[#8B5A2B] transition-colors">
                  +39 335 691 7918
                </a>
              </p>
              <p>
                <a href="mailto:info@helmutsantifaller.com" className="hover:text-[#8B5A2B] transition-colors">
                  info@helmutsantifaller.com
                </a>
              </p>
            </address>
            
            {/* Social Media Icons */}
            <div className="flex gap-4 mt-6">
              <a 
                href="https://www.facebook.com/santifaller.woodworks" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/60 hover:text-[#8B5A2B] transition-colors"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook className="h-5 w-5" strokeWidth={1.5} />
              </a>
              <a 
                href="https://it.pinterest.com/helmutsantifallerwoodworks/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/60 hover:text-[#8B5A2B] transition-colors"
                aria-label="Pinterest"
                data-testid="social-pinterest"
              >
                <PinterestIcon className="h-5 w-5" strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>

        <Separator className="my-12 bg-white/10" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-sans text-xs text-white/40">
            © {new Date().getFullYear()} Helmut Santifaller. {t(translations.footer.rights)}
          </p>
          <div className="flex gap-6">
            <Link
              to="/legal/impressum"
              className="font-sans text-xs text-white/40 hover:text-white/80 transition-colors"
              data-testid="footer-impressum"
            >
              {t(translations.footer.impressum)}
            </Link>
            <Link
              to="/legal/privacy"
              className="font-sans text-xs text-white/40 hover:text-white/80 transition-colors"
              data-testid="footer-privacy"
            >
              {t(translations.footer.privacy)}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
